/**
 * AnnotationInteractionHandler — thin event dispatcher for the annotation canvas.
 *
 * Converts DOM pointer events into AnnotationInteractionEvents and dispatches
 * them to annotation objects. Each annotation owns its own placement, drag,
 * and resize logic via the IAnnotationObject interface.
 */

import type { AnnotationLayer } from './annotation-layer';
import { ArrowAnnotation } from './objects/arrow-annotation';
import { EllipseAnnotation } from './objects/ellipse-annotation';
import { TextAnnotation } from './objects/text-annotation';
import type {
    AnnotationInteractionEvent,
    AnnotationStyle,
    AnnotationToolMode,
    IAnnotationObject,
} from './types';
import { DEFAULT_ANNOTATION_STYLE } from './types';

type Point = { x: number; y: number };

interface PlacingState {
    phase: 'placing';
    object: IAnnotationObject;
    startPoint: Point;
}

interface InteractingState {
    phase: 'interacting';
    object: IAnnotationObject;
    startPoint: Point;
    lastPoint: Point;
}

type InteractionState = { phase: 'idle' } | PlacingState | InteractingState;

export type AnnotationChangeCallback = () => void;

/** Request to start editing a text annotation in place (DOM overlay editor). */
export interface TextEditRequest {
    id: string;
    /** True when the annotation was just placed (empty text, discard on cancel). */
    isNew: boolean;
}

export class AnnotationInteractionHandler {
    private layer: AnnotationLayer;
    private state: InteractionState = { phase: 'idle' };
    private toolMode: AnnotationToolMode = 'select';
    private currentStyle: AnnotationStyle = { ...DEFAULT_ANNOTATION_STYLE };
    private selectedId: string | null = null;
    private onChange: AnnotationChangeCallback | null = null;
    private onTextEditRequest: ((request: TextEditRequest) => void) | null = null;
    private onToolModeChange: ((mode: AnnotationToolMode) => void) | null = null;

    private boundPointerDown = this.handlePointerDown.bind(this);
    private boundPointerMove = this.handlePointerMove.bind(this);
    private boundPointerUp = this.handlePointerUp.bind(this);
    private boundDoubleClick = this.handleDoubleClick.bind(this);
    private boundKeyDown = this.handleKeyDown.bind(this);

    constructor(layer: AnnotationLayer) {
        this.layer = layer;
        const c = layer.getCanvas();
        c.addEventListener('pointerdown', this.boundPointerDown);
        c.addEventListener('pointercancel', this.boundPointerUp);
        c.addEventListener('pointermove', this.boundPointerMove);
        c.addEventListener('pointerup', this.boundPointerUp);
        c.addEventListener('dblclick', this.boundDoubleClick);
        window.addEventListener('keydown', this.boundKeyDown);
    }

    // ── Public API ──

    setToolMode(mode: AnnotationToolMode): void {
        this.cancel();
        this.toolMode = mode;
        this.updateCursor();
    }

    getToolMode(): AnnotationToolMode {
        return this.toolMode;
    }

    setStyle(style: Partial<AnnotationStyle>): void {
        Object.assign(this.currentStyle, style);
    }

    getStyle(): AnnotationStyle {
        return { ...this.currentStyle };
    }

    getSelectedId(): string | null {
        return this.selectedId;
    }

    setSelectedId(id: string | null): void {
        this.selectObject(id);
    }

    /**
     * Single writer for selection state: updates `selectedId` and syncs the
     * `selected` flag on every object so the selected one renders persistent
     * handles, then re-renders. All selection changes must go through here.
     */
    private selectObject(id: string | null): void {
        this.selectedId = id;
        for (const obj of this.layer.getAllObjects()) {
            obj.selected = obj.id === id;
        }
        this.layer.render();
    }

    setOnChange(cb: AnnotationChangeCallback | null): void {
        this.onChange = cb;
    }

    setOnTextEditRequest(cb: ((request: TextEditRequest) => void) | null): void {
        this.onTextEditRequest = cb;
    }

    setOnToolModeChange(cb: ((mode: AnnotationToolMode) => void) | null): void {
        this.onToolModeChange = cb;
    }

    cancel(): void {
        if (this.state.phase === 'placing') {
            this.layer.removeObject(this.state.object.id);
        }
        this.state = { phase: 'idle' };
        this.layer.render();
    }

    dispose(): void {
        const c = this.layer.getCanvas();
        c.removeEventListener('pointerdown', this.boundPointerDown);
        c.removeEventListener('pointercancel', this.boundPointerUp);
        c.removeEventListener('pointermove', this.boundPointerMove);
        c.removeEventListener('pointerup', this.boundPointerUp);
        c.removeEventListener('dblclick', this.boundDoubleClick);
        window.removeEventListener('keydown', this.boundKeyDown);
    }

    // ── Pointer handlers ──

    private handlePointerDown(e: PointerEvent): void {
        if (this.state.phase !== 'idle') return;
        const pos = this.layer.canvasToImageCoords(e.clientX, e.clientY);

        switch (this.toolMode) {
            case 'arrow':
            case 'circle':
                this.startPlacing(this.toolMode, pos, e);
                break;
            case 'text':
                this.placeText(pos);
                break;
            case 'select':
                this.startSelectOrDrag(pos, e);
                break;
            case 'eraser':
                this.handleErase(pos);
                break;
        }
    }

    private handlePointerMove(e: PointerEvent): void {
        const pos = this.layer.canvasToImageCoords(e.clientX, e.clientY);

        switch (this.state.phase) {
            case 'idle':
                this.handleIdleHover(pos);
                break;
            case 'placing':
                this.state.object.updatePlacement(this.state.startPoint, pos);
                this.layer.render();
                break;
            case 'interacting':
                this.state.object.handleEvent(this.buildDragEvent('pointer_move', pos));
                this.state.lastPoint = { ...pos };
                this.layer.render();
                break;
        }
    }

    private handlePointerUp(e: PointerEvent): void {
        const pos = this.layer.canvasToImageCoords(e.clientX, e.clientY);

        if (this.state.phase === 'placing') {
            const { object, startPoint } = this.state;
            object.updatePlacement(startPoint, pos);
            if (object.isValidPlacement()) {
                this.selectObject(object.id);
            } else {
                this.layer.removeObject(object.id);
            }
            this.finalize();
        } else if (this.state.phase === 'interacting') {
            this.state.object.handleEvent(this.buildDragEvent('pointer_up', pos));
            this.finalize();
        }
    }

    /** Double-click on a text annotation (select tool) opens the in-place editor. */
    private handleDoubleClick(e: MouseEvent): void {
        if (this.toolMode !== 'select' || this.state.phase !== 'idle') return;
        const pos = this.layer.canvasToImageCoords(e.clientX, e.clientY);
        const hit = this.layer.hitTest(pos.x, pos.y);
        if (hit instanceof TextAnnotation) {
            this.selectObject(hit.id);
            this.onTextEditRequest?.({ id: hit.id, isNew: false });
        }
    }

    private handleKeyDown(e: KeyboardEvent): void {
        const target = e.target;
        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            (target instanceof HTMLElement && target.isContentEditable)
        ) {
            return;
        }

        if ((e.key === 'Delete' || e.key === 'Backspace') && this.state.phase === 'idle' && this.selectedId) {
            this.layer.removeObject(this.selectedId);
            this.selectObject(null);
            this.onChange?.();
        }
    }

    private handleIdleHover(pos: Point): void {
        if (this.toolMode !== 'select' && this.toolMode !== 'eraser') {
            this.updateCursor();
            return;
        }

        for (const obj of this.layer.getAllObjects()) obj.clearHoverState();
        const hit = this.layer.hitTest(pos.x, pos.y);

        if (hit) {
            hit.handleEvent(staticEvent('hover', pos));
            this.layer.getCanvas().style.cursor = this.toolMode === 'eraser' ? 'pointer' : 'move';
        } else {
            this.layer.getCanvas().style.cursor = 'default';
        }
        this.layer.render();
    }

    // ── Placement ──

    private startPlacing(tool: 'arrow' | 'circle', pos: Point, e: PointerEvent): void {
        const style = { ...this.currentStyle };
        const id = crypto.randomUUID();
        const object: IAnnotationObject =
            tool === 'arrow'
                ? new ArrowAnnotation({ id, start: { ...pos }, end: { ...pos }, style })
                : new EllipseAnnotation({ id, center: { ...pos }, radiusX: 0, radiusY: 0, style });

        this.layer.addObject(object);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        this.state = { phase: 'placing', object, startPoint: { ...pos } };
        this.layer.render();
    }

    private placeText(pos: Point): void {
        // Clicking an existing text annotation with the text tool edits it
        // instead of stacking a new annotation on top.
        const hit = this.layer.hitTest(pos.x, pos.y);
        if (hit instanceof TextAnnotation) {
            this.selectObject(hit.id);
            this.finalize();
            this.onTextEditRequest?.({ id: hit.id, isNew: false });
            return;
        }

        // Placed empty: renders as nothing until the in-place editor commits;
        // the editor discards the annotation if the text stays empty.
        const annotation = new TextAnnotation({
            id: crypto.randomUUID(),
            position: { ...pos },
            text: '',
            style: { ...this.currentStyle },
        });
        this.layer.addObject(annotation);
        this.selectObject(annotation.id);
        this.finalize();
        this.onTextEditRequest?.({ id: annotation.id, isNew: true });
    }

    // ── Select / drag ──

    private startSelectOrDrag(pos: Point, e: PointerEvent): void {
        const hit = this.layer.hitTest(pos.x, pos.y);
        if (!hit) {
            this.selectObject(null);
            this.finalize();
            return;
        }

        this.selectObject(hit.id);
        hit.handleEvent(staticEvent('pointer_down', pos));
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        this.state = {
            phase: 'interacting',
            object: hit,
            startPoint: { ...pos },
            lastPoint: { ...pos },
        };
    }

    // ── Eraser ──

    /**
     * Erase the object under the cursor. The eraser is "sticky": it stays the
     * active tool so the user can erase several objects in a row (unlike
     * placement/select, it does not switch back to the select tool).
     */
    private handleErase(pos: Point): void {
        const hit = this.layer.hitTest(pos.x, pos.y);
        if (!hit) return;
        this.layer.removeObject(hit.id);
        if (this.selectedId === hit.id) this.selectObject(null);
        this.onChange?.();
        this.layer.render();
    }

    // ── Helpers ──

    /** Reset to idle, emit change, re-render. */
    private finalize(): void {
        this.state = { phase: 'idle' };
        this.onToolModeChange?.('select');
        this.onChange?.();
        this.layer.render();
    }

    /** Build an event for an ongoing drag sequence (interacting state). */
    private buildDragEvent(type: AnnotationEventType, currentPoint: Point): AnnotationInteractionEvent {
        const { startPoint, lastPoint } = this.state as InteractingState;
        return {
            type,
            startPoint,
            lastPoint,
            currentPoint,
            delta: { x: currentPoint.x - lastPoint.x, y: currentPoint.y - lastPoint.y },
        };
    }

    private updateCursor(): void {
        const canvas = this.layer.getCanvas();
        canvas.style.cursor =
            this.toolMode === 'select' || this.toolMode === 'eraser' ? 'default' : 'crosshair';
    }
}

// ── Module-level helpers ──

type AnnotationEventType = AnnotationInteractionEvent['type'];

/** Create an event where all points are the same (no drag context). */
function staticEvent(type: AnnotationEventType, pos: Point): AnnotationInteractionEvent {
    return { type, startPoint: pos, lastPoint: pos, currentPoint: pos, delta: { x: 0, y: 0 } };
}
